// 慶祝粒子爆發與牌桌輕震 — 胡／自摸印記落定、槓字彈入時的打擊回饋
// 金星、金幣、閃光由爆發點向四周噴出，受重力下落並淡出；大爆發同時讓鏡頭短暫輕震。
//
// === 使用圖片 ===
// - public/assets/fx/fx-star-a.png / fx-star-b.png（key: FxKey.StarA / StarB）— 金色五角星
// - public/assets/fx/fx-coin-a.png / fx-coin-b.png（key: FxKey.CoinA / CoinB）— 方孔金幣
// - public/assets/fx/fx-sparkle-cream.png / fx-sparkle-red.png（key: FxKey.SparkleCream / SparkleRed）— 四角閃光

import type Phaser from 'phaser';

import { FxKey, type TFxKey } from '@scenes/boot-scene';

import { UIDepth } from './ui-depths';

// ---- 爆發粒子共用參數 ----
const BURST_DEPTH = UIDepth.CelebrationBurst; // 蓋過胡牌儀式與動作字樣
const BURST_SPEED_MIN = 220; // 粒子初速下限（px/s）
const BURST_GRAVITY_Y = 650; // 重力（讓金幣金星噴出後往下掉）
const BURST_LIFESPAN_MIN_MS = 900; // 粒子壽命下限
const BURST_LIFESPAN_MAX_MS = 1500; // 粒子壽命上限
const BURST_SCALE_END_RATE = 0.4; // 結束時縮成起始尺寸的比例
const BURST_SPIN_DEG = 360; // 粒子一生自轉角度
const BURST_CLEANUP_MS = BURST_LIFESPAN_MAX_MS + 300; // 最後一顆粒子消失後再銷毀 emitter

// ---- 鏡頭輕震（只用在大爆發） ----
const SHAKE_MS = 160; // 震動時長
const SHAKE_INTENSITY = 0.004; // 震幅（相對畫面尺寸）

export const BurstSize = {
  Large: 'large', // 自摸中央印記：整片金星金幣
  Medium: 'medium', // 胡牌門前印記
  Small: 'small', // 槓字彈入：少量閃光
} as const;
export type TBurstSize = (typeof BurstSize)[keyof typeof BurstSize];

type TBurstLayer = {
  readonly key: TFxKey;
  readonly count: number; // 該層粒子數
  readonly scale: number; // 起始縮放（素材約 130～180 px）
  readonly speedMax: number; // 初速上限（px/s）
};

const BURST_LAYERS: Record<TBurstSize, readonly TBurstLayer[]> = {
  [BurstSize.Large]: [
    { key: FxKey.StarA, count: 10, scale: 0.22, speedMax: 650 },
    { key: FxKey.StarB, count: 8, scale: 0.16, speedMax: 560 },
    { key: FxKey.CoinA, count: 8, scale: 0.18, speedMax: 600 },
    { key: FxKey.CoinB, count: 6, scale: 0.15, speedMax: 520 },
    { key: FxKey.SparkleCream, count: 10, scale: 0.2, speedMax: 700 },
    { key: FxKey.SparkleRed, count: 8, scale: 0.16, speedMax: 640 },
  ],
  [BurstSize.Medium]: [
    { key: FxKey.StarA, count: 6, scale: 0.16, speedMax: 480 },
    { key: FxKey.CoinA, count: 5, scale: 0.14, speedMax: 440 },
    { key: FxKey.SparkleCream, count: 6, scale: 0.15, speedMax: 520 },
    { key: FxKey.SparkleRed, count: 4, scale: 0.12, speedMax: 480 },
  ],
  [BurstSize.Small]: [
    { key: FxKey.SparkleCream, count: 8, scale: 0.18, speedMax: 280 },
    { key: FxKey.SparkleRed, count: 5, scale: 0.15, speedMax: 260 },
    { key: FxKey.StarB, count: 4, scale: 0.14, speedMax: 240 },
  ],
};

/** 在畫面座標 (x, y) 一次噴出整組粒子；emitter 於粒子全數消失後自動銷毀。 */
export const playCelebrationBurst = (
  scene: Phaser.Scene,
  x: number,
  y: number,
  size: TBurstSize
): void => {
  for (const layer of BURST_LAYERS[size]) {
    const emitter = scene.add.particles(x, y, layer.key, {
      speed: { min: BURST_SPEED_MIN, max: layer.speedMax },
      angle: { min: 0, max: 360 },
      gravityY: BURST_GRAVITY_Y,
      lifespan: { min: BURST_LIFESPAN_MIN_MS, max: BURST_LIFESPAN_MAX_MS },
      scale: { start: layer.scale, end: layer.scale * BURST_SCALE_END_RATE },
      rotate: { start: 0, end: BURST_SPIN_DEG },
      alpha: { start: 1, end: 0 },
      emitting: false,
    });
    emitter.setDepth(BURST_DEPTH);
    emitter.explode(layer.count);
    scene.time.delayedCall(BURST_CLEANUP_MS, () => emitter.destroy());
  }
};

/** 鏡頭短暫輕震，搭配大爆發營造落章的衝擊感。 */
export const shakeTable = (scene: Phaser.Scene): void => {
  scene.cameras.main.shake(SHAKE_MS, SHAKE_INTENSITY);
};
